import React from 'react';
import {useTranslation} from 'react-i18next';
import {Info} from '../../icons';
import {formatCompactMoney} from './panelFormatters';
import './BudgetDistributionTooltip.scss';

// Cuántas filas de líneas entran en el tooltip antes de resumir el resto. Un cliente con 75 líneas
// de control existe (Rouge) y listarlas todas convierte el tooltip en una pantalla: las que
// explican el número son las de arriba, y el resto se cuenta en una sola fila con su monto.
const MAX_ROWS = 6;

// Monto para ordenar: una línea sin presupuesto cargado (`null`) va al fondo, no al frente.
function amountOf(entry) {
    const value = Number(entry.amount);
    return Number.isFinite(value) ? value : -1;
}

/**
 * El ⓘ que explica cómo se compone el presupuesto del cliente (`kpis.budget`).
 *
 * Existe porque el número no se puede reconstruir mirando la pantalla. Dos motivos distintos, y el
 * tooltip tiene que cubrir LOS DOS:
 *
 * 1. **Hay jerarquía** (`basis: MAXIMAL_LINES`): una línea "Global" de $3.000 que abarca todas las
 *    cuentas, más una "Google" y una "Meta" de $1.000, dan **$3.000** — las específicas reparten el
 *    global, no se le suman. Sin explicación el usuario suma tres líneas a mano, le da $5.000 y deja
 *    de creerle al KPI.
 * 2. **No hay jerarquía** (`basis: SUM`): el total sí es la suma, pero de 75 líneas. Que la cuenta
 *    sea trivial no la hace verificable: el usuario tampoco puede confirmar de dónde salen $170,3M.
 *    Este era el caso sin ⓘ hasta 2026-08-01, y es el más frecuente.
 *
 * El árbol NO se calcula acá: viene armado del backend en `budget.distribution`, con el rol de cada
 * línea (`UMBRELLA` manda, `INSIDE` está adentro de otra, `STANDALONE` suma sola) y a quién reporta.
 * Recalcular la jerarquía en la pantalla sería una segunda fuente para el mismo veredicto — el
 * mismo error que ya nos costó tener dos criterios para el semáforo del gasto.
 */
export function BudgetDistributionTooltip({distribution, currency, partialOverlap, basis,
                                           perPlatform, linesWithoutBudget}) {
    const {t} = useTranslation();

    const entries = distribution || [];
    if (!entries.length) return null;

    // Las que suman, de mayor a menor: arriba quedan las que de verdad explican el total. El backend
    // las manda ordenadas por id (orden de creación), que no le dice nada a quien mira.
    const roots = entries.filter((entry) => entry.role !== 'INSIDE')
        .sort((first, second) => amountOf(second) - amountOf(first));
    const rootIds = new Set(roots.map((entry) => entry.id));
    const childrenOf = (rootId) => entries.filter(
        (entry) => entry.role === 'INSIDE' && entry.parent_id === rootId,
    );
    // Una línea abarcada que no cuelga de NINGUNA raíz no puede quedar invisible: se muestra al
    // final, sin sangría, antes que mentir por omisión sobre plata cargada. El criterio es "su padre
    // no es una raíz" y no "su padre no está en la lista": el backend garantiza un árbol de un solo
    // nivel (`containing_line_id` sólo devuelve maximales, y hay un test que lo fija), pero si
    // alguna vez llegara un nieto, con el criterio anterior desaparecía de la pantalla sin ruido.
    const orphans = entries.filter(
        (entry) => entry.role === 'INSIDE' && !rootIds.has(entry.parent_id),
    );

    // El cupo se gasta con las filas que se van a dibujar —una raíz arrastra a sus hijas—, así que
    // un cliente con jerarquía profunda no termina mostrando seis raíces y treinta hijas.
    //
    // Corta en el PRIMER desborde (`break`, no `continue`): si siguiera evaluando, una raíz chica
    // posterior entraría en el hueco que dejó una grande, y el tooltip mostraría las de $2.000
    // mientras la de $80.000 cae en el "+N". El orden por monto dejaría de significar nada.
    const shownRoots = [];
    let budgetedRows = 0;
    for (const root of roots) {
        const rows = 1 + childrenOf(root.id).length;
        // La primera entra siempre: una única raíz con muchas hijas dejaría el tooltip sin una sola
        // fila, que es peor que pasarse del cupo.
        if (shownRoots.length && budgetedRows + rows > MAX_ROWS) break;
        shownRoots.push(root);
        budgetedRows += rows;
    }
    // `shownRoots` es un prefijo de `roots` (el `break` lo garantiza), así que el resto es el corte.
    const hiddenRoots = roots.slice(shownRoots.length);
    // El MONTO suma sólo las raíces: las hijas no aportan al total y contarlas haría que las filas
    // del tooltip no cierren con el KPI. El CONTEO, en cambio, incluye a las hijas — el usuario
    // cuenta líneas de control, no roles del árbol, y "+ 3 líneas más" mintiendo sobre 8 ocultas es
    // el mismo error que motivó este tooltip. Que el monto no sea el de esas N líneas es justo lo
    // que explica la nota de abajo.
    const hiddenCount = hiddenRoots.reduce(
        (total, root) => total + 1 + childrenOf(root.id).length, 0,
    );
    const hiddenAmount = hiddenRoots.reduce(
        (total, root) => total + (Number(root.amount) || 0), 0,
    );

    const renderRow = (entry, isChild) => (
        <span className={`bdt-row${isChild ? ' -child' : ''}`} key={entry.id}>
            <span className="bdt-row__name">{entry.name}</span>
            <span className="bdt-row__amount">
                {formatCompactMoney(currency, entry.amount)
                    || t('client_panel.kpi_budget_tooltip_no_amount', 'sin presupuesto')}
            </span>
        </span>
    );

    // Con jerarquía la nota explica el reparto; sin jerarquía, que el total es la suma y de cuántas
    // líneas. Un "no se suman" en un cliente donde sí se suman sería peor que no decir nada.
    const renderBasisNote = () => {
        if (basis === 'MAXIMAL_LINES') {
            return t('client_panel.kpi_budget_tooltip_note',
                'Las líneas específicas reparten el presupuesto de la que las abarca: no se suman.');
        }
        return t('client_panel.kpi_budget_tooltip_note_sum', {
            count: roots.length,
            defaultValue: 'Es el presupuesto de la única línea de seguimiento del cliente.',
            defaultValue_other: 'Es la suma de las {{count}} líneas de seguimiento del cliente.',
        });
    };

    const without = Number(linesWithoutBudget || 0);

    return (
        <span className="info-icon-container pnl-kpi__info">
            <Info size={13} weight="bold"/>
            <span className="warning-tooltip -rich">
                <span className="warning-tooltip-body">
                    <span className="bdt">
                        <span className="bdt-title">
                            {t('client_panel.kpi_budget_tooltip_title', 'Cómo se compone')}
                        </span>
                        {shownRoots.map((root) => (
                            <React.Fragment key={root.id}>
                                {renderRow(root, false)}
                                {childrenOf(root.id).map((child) => renderRow(child, true))}
                            </React.Fragment>
                        ))}
                        {hiddenRoots.length > 0 && (
                            <span className="bdt-row -rest">
                                <span className="bdt-row__name">
                                    {t('client_panel.kpi_budget_tooltip_rest', {
                                        count: hiddenCount,
                                        defaultValue: '+ {{count}} línea más',
                                        defaultValue_other: '+ {{count}} líneas más',
                                    })}
                                </span>
                                <span className="bdt-row__amount">
                                    {formatCompactMoney(currency, hiddenAmount)}
                                </span>
                            </span>
                        )}
                        {orphans.map((orphan) => renderRow(orphan, false))}

                        <span className="bdt-note">{renderBasisNote()}</span>
                        {without > 0 && (
                            <span className="bdt-note">
                                {t('client_panel.kpi_budget_tooltip_without', {
                                    count: without,
                                    defaultValue: '{{count}} línea todavía no tiene presupuesto cargado.',
                                    defaultValue_other: '{{count}} líneas todavía no tienen presupuesto cargado.',
                                })}
                            </span>
                        )}
                        {/* "definido por plataforma" aparece en el subtítulo del KPI y por sí solo no
                            dice nada: acá se explica qué significa. */}
                        {perPlatform && (
                            <span className="bdt-note">
                                {t('client_panel.kpi_budget_tooltip_per_platform',
                                    'Hay líneas con un presupuesto propio por plataforma.')}
                            </span>
                        )}
                        {partialOverlap && (
                            <span className="bdt-note -warn">
                                {t('client_panel.kpi_budget_tooltip_partial_overlap',
                                    'Hay líneas que se superponen parcialmente: el total las suma igual.')}
                            </span>
                        )}
                    </span>
                </span>
            </span>
        </span>
    );
}
